import type { AxModelInfo } from '../types.js';

import { AxAIOpenAIEmbedModel, AxAIOpenAIModel } from './chat_types.js';
import { AxAIOpenAIResponsesModel } from './responses_types.js';

/**
 * OpenAI: Model information
 */
export const axModelInfoOpenAI: AxModelInfo[] = [
  // Not Reasoning models
  {
    name: AxAIOpenAIModel.GPT4,
    currency: 'usd',
    promptTokenCostPer1M: 30,
    completionTokenCostPer1M: 60,
  },
  {
    name: AxAIOpenAIModel.GPT41,
    currency: 'usd',
    promptTokenCostPer1M: 2,
    completionTokenCostPer1M: 8,
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.GPT41Mini,
    currency: 'usd',
    promptTokenCostPer1M: 0.4,
    completionTokenCostPer1M: 1.6,
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.GPT41Nano,
    currency: 'usd',
    promptTokenCostPer1M: 0.1,
    completionTokenCostPer1M: 0.4,
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.GPT4O,
    currency: 'usd',
    promptTokenCostPer1M: 2.5,
    completionTokenCostPer1M: 10,
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.GPT4OMini,
    currency: 'usd',
    promptTokenCostPer1M: 0.15,
    completionTokenCostPer1M: 0.6,
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.GPTAudio,
    audio: { input: true, output: true },
  },
  {
    name: AxAIOpenAIModel.GPTAudioMini,
    audio: { input: true, output: true },
  },
  {
    name: AxAIOpenAIModel.GPTAudio15,
    audio: { input: true, output: true },
  },
  {
    name: AxAIOpenAIModel.GPTRealtime15,
    audio: { input: true, output: true },
  },
  {
    name: AxAIOpenAIModel.GPTRealtime2,
    audio: { input: true, output: true },
    supported: { thinkingBudget: true },
  },
  {
    name: AxAIOpenAIModel.GPTRealtime21,
    audio: { input: true, output: true },
    supported: { thinkingBudget: true },
  },
  {
    name: AxAIOpenAIModel.GPTRealtime21Mini,
    audio: { input: true, output: true },
    supported: { thinkingBudget: true },
  },
  {
    name: AxAIOpenAIModel.GPTRealtimeWhisper,
    audio: { input: true, output: false },
  },
  {
    name: AxAIOpenAIModel.GPTRealtimeTranslate,
    audio: { input: true, output: true },
  },
  // Billed per minute of audio rather than per token, so no token prices.
  {
    name: AxAIOpenAIModel.GPTTranscribe,
    audio: { input: true, output: false },
    supported: { operations: ['transcribe'], serviceTiers: [] },
  },
  {
    name: AxAIOpenAIModel.GPT4ChatGPT4O,
    currency: 'usd',
    promptTokenCostPer1M: 5,
    completionTokenCostPer1M: 15,
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.GPT4Turbo,
    currency: 'usd',
    promptTokenCostPer1M: 10,
    completionTokenCostPer1M: 30,
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.GPT35Turbo,
    currency: 'usd',
    promptTokenCostPer1M: 0.5,
    completionTokenCostPer1M: 1.5,
  },
  // GPT-5 models
  {
    name: AxAIOpenAIModel.GPT5Nano,
    currency: 'usd',
    promptTokenCostPer1M: 0.05,
    completionTokenCostPer1M: 0.4,
    notSupported: { temperature: true, topP: true },
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.GPT5Mini,
    currency: 'usd',
    promptTokenCostPer1M: 0.25,
    completionTokenCostPer1M: 2,
    notSupported: { temperature: true, topP: true },
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.GPT5,
    currency: 'usd',
    promptTokenCostPer1M: 1.25,
    completionTokenCostPer1M: 10,
    notSupported: { temperature: true, topP: true },
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.GPT5Chat,
    currency: 'usd',
    promptTokenCostPer1M: 1.25,
    completionTokenCostPer1M: 10,
    notSupported: { temperature: true, topP: true },
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.GPT5ChatLatest,
    currency: 'usd',
    promptTokenCostPer1M: 1.25,
    completionTokenCostPer1M: 10,
    notSupported: { temperature: true, topP: true },
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.GPT5Pro,
    currency: 'usd',
    promptTokenCostPer1M: 15,
    completionTokenCostPer1M: 120,
    notSupported: { temperature: true, topP: true },
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.GPT5Codex,
    currency: 'usd',
    promptTokenCostPer1M: 1.25,
    completionTokenCostPer1M: 10,
    notSupported: { temperature: true, topP: true },
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.GPT51,
    currency: 'usd',
    promptTokenCostPer1M: 1.25,
    completionTokenCostPer1M: 10,
    notSupported: { temperature: true, topP: true },
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.GPT51ChatLatest,
    currency: 'usd',
    promptTokenCostPer1M: 1.25,
    completionTokenCostPer1M: 10,
    notSupported: { temperature: true, topP: true },
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.GPT51Codex,
    currency: 'usd',
    promptTokenCostPer1M: 1.25,
    completionTokenCostPer1M: 10,
    notSupported: { temperature: true, topP: true },
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.GPT51CodexMini,
    currency: 'usd',
    promptTokenCostPer1M: 0.25,
    completionTokenCostPer1M: 2,
    notSupported: { temperature: true, topP: true },
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.GPT51CodexMax,
    currency: 'usd',
    promptTokenCostPer1M: 1.25,
    completionTokenCostPer1M: 10,
    notSupported: { temperature: true, topP: true },
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.GPT52,
    currency: 'usd',
    promptTokenCostPer1M: 1.75,
    completionTokenCostPer1M: 14,
    notSupported: { temperature: true, topP: true },
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.GPT52ChatLatest,
    currency: 'usd',
    promptTokenCostPer1M: 1.75,
    completionTokenCostPer1M: 14,
    notSupported: { temperature: true, topP: true },
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.GPT52Codex,
    currency: 'usd',
    promptTokenCostPer1M: 1.75,
    completionTokenCostPer1M: 14,
    notSupported: { temperature: true, topP: true },
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.GPT52Pro,
    currency: 'usd',
    promptTokenCostPer1M: 21,
    completionTokenCostPer1M: 168,
    notSupported: { temperature: true, topP: true },
    supported: { structuredOutputs: true },
  },
  // GPT-5.4 models
  {
    name: AxAIOpenAIModel.GPT54,
    currency: 'usd',
    promptTokenCostPer1M: 2.5,
    completionTokenCostPer1M: 15,
    notSupported: { temperature: true, topP: true },
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.GPT54Mini,
    currency: 'usd',
    promptTokenCostPer1M: 0.75,
    completionTokenCostPer1M: 4.5,
    notSupported: { temperature: true, topP: true },
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.GPT54Nano,
    currency: 'usd',
    promptTokenCostPer1M: 0.2,
    completionTokenCostPer1M: 1.25,
    notSupported: { temperature: true, topP: true },
    supported: { structuredOutputs: true },
  },
  // GPT-5.5 models
  {
    name: AxAIOpenAIModel.GPT55,
    currency: 'usd',
    promptTokenCostPer1M: 5,
    completionTokenCostPer1M: 30,
    cacheReadTokenCostPer1M: 0.5,
    longContextThreshold: 272_000,
    longContextPromptTokenCostPer1M: 10,
    longContextCompletionTokenCostPer1M: 45,
    longContextCacheReadTokenCostPer1M: 1,
    contextWindow: 1_000_000,
    notSupported: { temperature: true, topP: true },
    supported: { structuredOutputs: true, thinkingBudget: true },
  },
  {
    name: AxAIOpenAIModel.GPT55Pro,
    currency: 'usd',
    promptTokenCostPer1M: 30,
    completionTokenCostPer1M: 180,
    longContextThreshold: 272_000,
    longContextPromptTokenCostPer1M: 60,
    longContextCompletionTokenCostPer1M: 270,
    contextWindow: 1_000_000,
    isExpensive: true,
    notSupported: { temperature: true, topP: true },
    supported: { structuredOutputs: true, thinkingBudget: true },
  },
  // GPT-6 models. Above 272K input tokens the whole request is billed at 2x
  // input and cache / 1.5x output across the family.
  {
    name: AxAIOpenAIModel.GPT6Astra,
    currency: 'usd',
    promptTokenCostPer1M: 10,
    completionTokenCostPer1M: 50,
    cacheReadTokenCostPer1M: 1,
    cacheWriteTokenCostPer1M: 12.5,
    longContextThreshold: 272_000,
    longContextPromptTokenCostPer1M: 20,
    longContextCompletionTokenCostPer1M: 75,
    longContextCacheReadTokenCostPer1M: 2,
    longContextCacheWriteTokenCostPer1M: 25,
    contextWindow: 1_050_000,
    maxTokens: 128_000,
    notSupported: { temperature: true, topP: true },
    supported: {
      structuredOutputs: true,
      thinkingBudget: true,
      structuredOutputModes: ['native', 'json_object'],
    },
  },
  {
    name: AxAIOpenAIModel.GPT6Sol,
    currency: 'usd',
    promptTokenCostPer1M: 2,
    completionTokenCostPer1M: 10,
    cacheReadTokenCostPer1M: 0.2,
    cacheWriteTokenCostPer1M: 2.5,
    longContextThreshold: 272_000,
    longContextPromptTokenCostPer1M: 4,
    longContextCompletionTokenCostPer1M: 15,
    longContextCacheReadTokenCostPer1M: 0.4,
    longContextCacheWriteTokenCostPer1M: 5,
    contextWindow: 1_050_000,
    maxTokens: 128_000,
    notSupported: { temperature: true, topP: true },
    supported: {
      structuredOutputs: true,
      thinkingBudget: true,
      structuredOutputModes: ['native', 'json_object'],
    },
  },
  {
    name: AxAIOpenAIModel.GPT6Luna,
    currency: 'usd',
    promptTokenCostPer1M: 0.1,
    completionTokenCostPer1M: 0.5,
    cacheReadTokenCostPer1M: 0.01,
    cacheWriteTokenCostPer1M: 0.125,
    longContextThreshold: 272_000,
    longContextPromptTokenCostPer1M: 0.2,
    longContextCompletionTokenCostPer1M: 0.75,
    longContextCacheReadTokenCostPer1M: 0.02,
    longContextCacheWriteTokenCostPer1M: 0.25,
    contextWindow: 1_050_000,
    maxTokens: 128_000,
    notSupported: { temperature: true, topP: true },
    supported: {
      structuredOutputs: true,
      thinkingBudget: true,
      structuredOutputModes: ['native', 'json_object'],
    },
  },
  // GPT-5.6 models. Above 272K input tokens the whole request is billed at 2x
  // input / 1.5x output across the family.
  {
    name: AxAIOpenAIModel.GPT56Sol,
    aliases: [AxAIOpenAIModel.GPT56],
    currency: 'usd',
    promptTokenCostPer1M: 4,
    completionTokenCostPer1M: 20,
    cacheReadTokenCostPer1M: 0.4,
    cacheWriteTokenCostPer1M: 5,
    longContextThreshold: 272_000,
    longContextPromptTokenCostPer1M: 8,
    longContextCompletionTokenCostPer1M: 30,
    longContextCacheReadTokenCostPer1M: 0.8,
    contextWindow: 1_050_000,
    maxTokens: 128_000,
    notSupported: { temperature: true, topP: true },
    supported: { structuredOutputs: true, thinkingBudget: true },
  },
  {
    name: AxAIOpenAIModel.GPT56Terra,
    currency: 'usd',
    promptTokenCostPer1M: 2,
    completionTokenCostPer1M: 12,
    cacheReadTokenCostPer1M: 0.2,
    cacheWriteTokenCostPer1M: 2.5,
    longContextThreshold: 272_000,
    longContextPromptTokenCostPer1M: 4,
    longContextCompletionTokenCostPer1M: 18,
    longContextCacheReadTokenCostPer1M: 0.4,
    contextWindow: 1_050_000,
    maxTokens: 128_000,
    notSupported: { temperature: true, topP: true },
    supported: { structuredOutputs: true, thinkingBudget: true },
  },
  {
    name: AxAIOpenAIModel.GPT56Luna,
    currency: 'usd',
    promptTokenCostPer1M: 0.2,
    completionTokenCostPer1M: 1.2,
    cacheReadTokenCostPer1M: 0.02,
    cacheWriteTokenCostPer1M: 0.25,
    longContextThreshold: 272_000,
    longContextPromptTokenCostPer1M: 0.4,
    longContextCompletionTokenCostPer1M: 1.8,
    longContextCacheReadTokenCostPer1M: 0.04,
    contextWindow: 1_050_000,
    maxTokens: 128_000,
    notSupported: { temperature: true, topP: true },
    supported: { structuredOutputs: true, thinkingBudget: true },
  },
  // Reasoning models
  {
    name: AxAIOpenAIModel.O1,
    currency: 'usd',
    promptTokenCostPer1M: 15,
    completionTokenCostPer1M: 60,
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.O1Mini,
    currency: 'usd',
    promptTokenCostPer1M: 1.1,
    completionTokenCostPer1M: 4.4,
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.O3,
    currency: 'usd',
    promptTokenCostPer1M: 2,
    completionTokenCostPer1M: 8,
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIModel.O4Mini,
    currency: 'usd',
    promptTokenCostPer1M: 1.1,
    completionTokenCostPer1M: 4.4,
    supported: { structuredOutputs: true },
  },
  // Embedding models
  {
    name: AxAIOpenAIEmbedModel.TextEmbeddingAda002,
    currency: 'usd',
    promptTokenCostPer1M: 0.1,
    completionTokenCostPer1M: 0.1,
  },
  {
    name: AxAIOpenAIEmbedModel.TextEmbedding3Small,
    currency: 'usd',
    promptTokenCostPer1M: 0.02,
    completionTokenCostPer1M: 0.02,
  },
  {
    name: AxAIOpenAIEmbedModel.TextEmbedding3Large,
    currency: 'usd',
    promptTokenCostPer1M: 0.13,
    completionTokenCostPer1M: 0.13,
  },
];

/**
 * OpenAI: Model information
 */
export const axModelInfoOpenAIResponses: AxModelInfo[] = [
  // Not Reasoning models
  {
    name: AxAIOpenAIResponsesModel.GPT4,
    currency: 'usd',
    promptTokenCostPer1M: 30,
    completionTokenCostPer1M: 60,
  },
  {
    name: AxAIOpenAIResponsesModel.GPT41,
    currency: 'usd',
    promptTokenCostPer1M: 2,
    completionTokenCostPer1M: 8,
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT41Mini,
    currency: 'usd',
    promptTokenCostPer1M: 0.4,
    completionTokenCostPer1M: 1.6,
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT41Nano,
    currency: 'usd',
    promptTokenCostPer1M: 0.1,
    completionTokenCostPer1M: 0.4,
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT4O,
    currency: 'usd',
    promptTokenCostPer1M: 2.5,
    completionTokenCostPer1M: 10,
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT4OMini,
    currency: 'usd',
    promptTokenCostPer1M: 0.15,
    completionTokenCostPer1M: 0.6,
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT4ChatGPT4O,
    currency: 'usd',
    promptTokenCostPer1M: 5,
    completionTokenCostPer1M: 15,
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT4Turbo,
    currency: 'usd',
    promptTokenCostPer1M: 10,
    completionTokenCostPer1M: 30,
    supported: { structuredOutputs: true },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT35Turbo,
    currency: 'usd',
    promptTokenCostPer1M: 0.5,
    completionTokenCostPer1M: 1.5,
  },
  // GPT-5 models
  {
    name: AxAIOpenAIResponsesModel.GPT5Nano,
    currency: 'usd',
    promptTokenCostPer1M: 0.05,
    completionTokenCostPer1M: 0.4,
    notSupported: { temperature: true, topP: true },
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT5Mini,
    currency: 'usd',
    promptTokenCostPer1M: 0.25,
    completionTokenCostPer1M: 2,
    notSupported: { temperature: true, topP: true },
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT5,
    currency: 'usd',
    promptTokenCostPer1M: 1.25,
    completionTokenCostPer1M: 10,
    notSupported: { temperature: true, topP: true },
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT5Chat,
    currency: 'usd',
    promptTokenCostPer1M: 1.25,
    completionTokenCostPer1M: 10,
    notSupported: { temperature: true, topP: true },
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT5ChatLatest,
    currency: 'usd',
    promptTokenCostPer1M: 1.25,
    completionTokenCostPer1M: 10,
    notSupported: { temperature: true, topP: true },
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT5Pro,
    currency: 'usd',
    promptTokenCostPer1M: 15,
    completionTokenCostPer1M: 120,
    notSupported: { temperature: true, topP: true },
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT5Codex,
    currency: 'usd',
    promptTokenCostPer1M: 1.25,
    completionTokenCostPer1M: 10,
    notSupported: { temperature: true, topP: true },
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT51,
    currency: 'usd',
    promptTokenCostPer1M: 1.25,
    completionTokenCostPer1M: 10,
    notSupported: { temperature: true, topP: true },
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT51ChatLatest,
    currency: 'usd',
    promptTokenCostPer1M: 1.25,
    completionTokenCostPer1M: 10,
    notSupported: { temperature: true, topP: true },
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT51Codex,
    currency: 'usd',
    promptTokenCostPer1M: 1.25,
    completionTokenCostPer1M: 10,
    notSupported: { temperature: true, topP: true },
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT51CodexMini,
    currency: 'usd',
    promptTokenCostPer1M: 0.25,
    completionTokenCostPer1M: 2,
    notSupported: { temperature: true, topP: true },
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT51CodexMax,
    currency: 'usd',
    promptTokenCostPer1M: 1.25,
    completionTokenCostPer1M: 10,
    notSupported: { temperature: true, topP: true },
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT52,
    currency: 'usd',
    promptTokenCostPer1M: 1.75,
    completionTokenCostPer1M: 14,
    notSupported: { temperature: true, topP: true },
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT52ChatLatest,
    currency: 'usd',
    promptTokenCostPer1M: 1.75,
    completionTokenCostPer1M: 14,
    notSupported: { temperature: true, topP: true },
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT52Codex,
    currency: 'usd',
    promptTokenCostPer1M: 1.75,
    completionTokenCostPer1M: 14,
    notSupported: { temperature: true, topP: true },
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT52Pro,
    currency: 'usd',
    promptTokenCostPer1M: 21,
    completionTokenCostPer1M: 168,
    notSupported: { temperature: true, topP: true },
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  // GPT-5.4 models
  {
    name: AxAIOpenAIResponsesModel.GPT54,
    currency: 'usd',
    promptTokenCostPer1M: 2.5,
    completionTokenCostPer1M: 15,
    notSupported: { temperature: true, topP: true },
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT54Mini,
    currency: 'usd',
    promptTokenCostPer1M: 0.75,
    completionTokenCostPer1M: 4.5,
    notSupported: { temperature: true, topP: true },
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT54Nano,
    currency: 'usd',
    promptTokenCostPer1M: 0.2,
    completionTokenCostPer1M: 1.25,
    notSupported: { temperature: true, topP: true },
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  // GPT-5.5 models
  {
    name: AxAIOpenAIResponsesModel.GPT55,
    currency: 'usd',
    promptTokenCostPer1M: 5,
    completionTokenCostPer1M: 30,
    cacheReadTokenCostPer1M: 0.5,
    longContextThreshold: 272_000,
    longContextPromptTokenCostPer1M: 10,
    longContextCompletionTokenCostPer1M: 45,
    longContextCacheReadTokenCostPer1M: 1,
    contextWindow: 1_000_000,
    notSupported: { temperature: true, topP: true },
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT55Pro,
    currency: 'usd',
    promptTokenCostPer1M: 30,
    completionTokenCostPer1M: 180,
    longContextThreshold: 272_000,
    longContextPromptTokenCostPer1M: 60,
    longContextCompletionTokenCostPer1M: 270,
    contextWindow: 1_000_000,
    isExpensive: true,
    notSupported: { temperature: true, topP: true },
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  // GPT-6 models. Above 272K input tokens the whole request is billed at 2x
  // input and cache / 1.5x output across the family.
  {
    name: AxAIOpenAIResponsesModel.GPT6Astra,
    currency: 'usd',
    promptTokenCostPer1M: 10,
    completionTokenCostPer1M: 50,
    cacheReadTokenCostPer1M: 1,
    cacheWriteTokenCostPer1M: 12.5,
    longContextThreshold: 272_000,
    longContextPromptTokenCostPer1M: 20,
    longContextCompletionTokenCostPer1M: 75,
    longContextCacheReadTokenCostPer1M: 2,
    longContextCacheWriteTokenCostPer1M: 25,
    contextWindow: 1_050_000,
    maxTokens: 128_000,
    notSupported: { temperature: true, topP: true },
    supported: {
      structuredOutputs: true,
      thinkingBudget: true,
      showThoughts: true,
    },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT6Sol,
    currency: 'usd',
    promptTokenCostPer1M: 2,
    completionTokenCostPer1M: 10,
    cacheReadTokenCostPer1M: 0.2,
    cacheWriteTokenCostPer1M: 2.5,
    longContextThreshold: 272_000,
    longContextPromptTokenCostPer1M: 4,
    longContextCompletionTokenCostPer1M: 15,
    longContextCacheReadTokenCostPer1M: 0.4,
    longContextCacheWriteTokenCostPer1M: 5,
    contextWindow: 1_050_000,
    maxTokens: 128_000,
    notSupported: { temperature: true, topP: true },
    supported: {
      structuredOutputs: true,
      thinkingBudget: true,
      showThoughts: true,
    },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT6Luna,
    currency: 'usd',
    promptTokenCostPer1M: 0.1,
    completionTokenCostPer1M: 0.5,
    cacheReadTokenCostPer1M: 0.01,
    cacheWriteTokenCostPer1M: 0.125,
    longContextThreshold: 272_000,
    longContextPromptTokenCostPer1M: 0.2,
    longContextCompletionTokenCostPer1M: 0.75,
    longContextCacheReadTokenCostPer1M: 0.02,
    longContextCacheWriteTokenCostPer1M: 0.25,
    contextWindow: 1_050_000,
    maxTokens: 128_000,
    notSupported: { temperature: true, topP: true },
    supported: {
      structuredOutputs: true,
      thinkingBudget: true,
      showThoughts: true,
    },
  },
  // GPT-5.6 models. Above 272K input tokens the whole request is billed at 2x
  // input / 1.5x output across the family.
  {
    name: AxAIOpenAIResponsesModel.GPT56Sol,
    aliases: [AxAIOpenAIResponsesModel.GPT56],
    currency: 'usd',
    promptTokenCostPer1M: 4,
    completionTokenCostPer1M: 20,
    cacheReadTokenCostPer1M: 0.4,
    cacheWriteTokenCostPer1M: 5,
    longContextThreshold: 272_000,
    longContextPromptTokenCostPer1M: 8,
    longContextCompletionTokenCostPer1M: 30,
    longContextCacheReadTokenCostPer1M: 0.8,
    contextWindow: 1_050_000,
    maxTokens: 128_000,
    notSupported: { temperature: true, topP: true },
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT56Terra,
    currency: 'usd',
    promptTokenCostPer1M: 2,
    completionTokenCostPer1M: 12,
    cacheReadTokenCostPer1M: 0.2,
    cacheWriteTokenCostPer1M: 2.5,
    longContextThreshold: 272_000,
    longContextPromptTokenCostPer1M: 4,
    longContextCompletionTokenCostPer1M: 18,
    longContextCacheReadTokenCostPer1M: 0.4,
    contextWindow: 1_050_000,
    maxTokens: 128_000,
    notSupported: { temperature: true, topP: true },
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  {
    name: AxAIOpenAIResponsesModel.GPT56Luna,
    currency: 'usd',
    promptTokenCostPer1M: 0.2,
    completionTokenCostPer1M: 1.2,
    cacheReadTokenCostPer1M: 0.02,
    cacheWriteTokenCostPer1M: 0.25,
    longContextThreshold: 272_000,
    longContextPromptTokenCostPer1M: 0.4,
    longContextCompletionTokenCostPer1M: 1.8,
    longContextCacheReadTokenCostPer1M: 0.04,
    contextWindow: 1_050_000,
    maxTokens: 128_000,
    notSupported: { temperature: true, topP: true },
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  // Reasoning models
  {
    name: AxAIOpenAIResponsesModel.O1Pro,
    currency: 'usd',
    promptTokenCostPer1M: 150,
    completionTokenCostPer1M: 600,
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
    isExpensive: true,
  },
  {
    name: AxAIOpenAIResponsesModel.O1,
    currency: 'usd',
    promptTokenCostPer1M: 15,
    completionTokenCostPer1M: 60,
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  {
    name: AxAIOpenAIResponsesModel.O3Pro,
    currency: 'usd',
    promptTokenCostPer1M: 20,
    completionTokenCostPer1M: 80,
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
    isExpensive: true,
  },
  {
    name: AxAIOpenAIResponsesModel.O3,
    currency: 'usd',
    promptTokenCostPer1M: 2,
    completionTokenCostPer1M: 8,
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  {
    name: AxAIOpenAIModel.O3Mini,
    currency: 'usd',
    promptTokenCostPer1M: 1.1,
    completionTokenCostPer1M: 4.4,
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
  {
    name: AxAIOpenAIResponsesModel.O4Mini,
    currency: 'usd',
    promptTokenCostPer1M: 1.1,
    completionTokenCostPer1M: 4.4,
    supported: {
      thinkingBudget: true,
      showThoughts: true,
      structuredOutputs: true,
    },
  },
];
